/**
 * Incident Timeline - Real-time safety incident feed
 * Twitter-like scrolling feed of local safety events
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  AlertTriangle,
  Shield,
  Radio,
  Cloud,
  Users,
  Clock,
  MapPin,
  ChevronRight,
  RefreshCw,
  Filter,
  Search,
  Flame,
  Car,
  Siren,
  HeartPulse,
  Eye,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Incident type icons
const INCIDENT_ICONS = {
  robbery: Shield,
  assault: AlertTriangle,
  fire: Flame,
  accident: Car,
  medical: HeartPulse,
  pursuit: Siren,
  shots_fired: AlertTriangle,
  domestic: Users,
  traffic_stop: Car,
  weather: Cloud,
  other: Radio
};

// Priority colors
const PRIORITY_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/50',
  urgent: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  routine: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  informational: 'bg-slate-500/20 text-slate-400 border-slate-500/50'
};

// Source badges
const SOURCE_BADGES = {
  scanner: { label: 'Scanner', color: 'bg-teal-600' },
  weather: { label: 'NWS', color: 'bg-blue-600' },
  community: { label: 'Community', color: 'bg-purple-600' },
  camera: { label: 'Camera', color: 'bg-amber-600' }
};

const IncidentCard = ({ incident, onSelect }) => {
  const Icon = INCIDENT_ICONS[incident.type] || Radio;
  const priorityClass = PRIORITY_COLORS[incident.priority] || PRIORITY_COLORS.informational;
  const sourceBadge = SOURCE_BADGES[incident.source] || SOURCE_BADGES.scanner;

  return (
    <div 
      className={`p-3 rounded-lg border ${priorityClass} cursor-pointer hover:scale-[1.02] transition-all`}
      onClick={() => onSelect?.(incident)}
      data-testid={`incident-card-${incident.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${priorityClass}`}>
          <Icon className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={`${sourceBadge.color} text-white text-[10px] px-1.5`}>
              {sourceBadge.label}
            </Badge>
            <span className="text-[10px] text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {incident.time_ago}
            </span>
          </div>
          
          <h4 className="text-sm font-medium text-slate-200 line-clamp-1">
            {incident.title}
          </h4>
          
          <p className="text-xs text-slate-400 line-clamp-2 mt-1">
            {incident.description}
          </p>
          
          {incident.location?.address && (
            <div className="flex items-center gap-1 mt-2 text-[10px] text-slate-500">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{incident.location.address}</span>
            </div>
          )}
          
          {incident.metadata?.tank_assessment && (
            <div className="mt-2 p-2 rounded bg-slate-800/50 border border-teal-500/30">
              <p className="text-[10px] text-teal-400 font-medium">Tank's Assessment:</p>
              <p className="text-[10px] text-slate-300">{incident.metadata.tank_assessment}</p>
            </div>
          )}
        </div>
        
        <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
      </div>
    </div>
  );
};

const TimelineStats = ({ stats }) => {
  if (!stats) return null;

  return (
    <div className="grid grid-cols-3 gap-2 p-3 bg-slate-800/50 rounded-lg mb-4">
      <div className="text-center">
        <p className="text-lg font-bold text-white">{stats.total || 0}</p>
        <p className="text-[10px] text-slate-400">Total</p>
      </div>
      <div className="text-center">
        <p className="text-lg font-bold text-red-400">{stats.critical_urgent || 0}</p>
        <p className="text-[10px] text-slate-400">Critical</p>
      </div>
      <div className="text-center">
        <p className="text-lg font-bold text-teal-400">
          {Object.keys(stats.by_source || {}).length}
        </p>
        <p className="text-[10px] text-slate-400">Sources</p>
      </div>
    </div>
  );
};

export const IncidentTimelinePanel = ({ onIncidentSelect }) => {
  const { token } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    types: [],
    sources: [],
    priority: [],
    hours: 24
  });
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTimeline = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.types.length) params.append('types', filters.types.join(','));
      if (filters.sources.length) params.append('sources', filters.sources.join(','));
      if (filters.priority.length) params.append('priority', filters.priority.join(','));
      params.append('hours', filters.hours);
      params.append('limit', 100);

      const response = await fetch(`${API_URL}/api/safety/timeline?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIncidents(data.incidents || []);
      }
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/safety/timeline/stats?hours=24`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchTimeline();
    fetchStats();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchTimeline, 30000);
    return () => clearInterval(interval);
  }, [fetchTimeline, fetchStats]);

  const filteredIncidents = incidents.filter(incident => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      incident.title?.toLowerCase().includes(query) ||
      incident.description?.toLowerCase().includes(query) ||
      incident.location?.address?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="h-full flex flex-col" data-testid="incident-timeline-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-teal-400" />
            Live Timeline
          </h3>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchTimeline}
            className="text-slate-400 hover:text-white"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search incidents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-slate-800/50 border-slate-700 text-sm"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 pt-4">
        <TimelineStats stats={stats} />
      </div>

      {/* Incident Feed */}
      <ScrollArea className="flex-1 px-4">
        <div className="space-y-3 pb-4">
          {loading && incidents.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-teal-400 animate-spin" />
            </div>
          ) : filteredIncidents.length > 0 ? (
            filteredIncidents.map(incident => (
              <IncidentCard 
                key={incident.id} 
                incident={incident}
                onSelect={onIncidentSelect}
              />
            ))
          ) : (
            <div className="text-center py-8">
              <Radio className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No incidents found</p>
              <p className="text-xs text-slate-500">Check back later for updates</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Quick Filters */}
      <div className="p-3 border-t border-slate-700 flex gap-2 flex-wrap">
        {['critical', 'urgent', 'scanner', 'weather'].map(filter => (
          <Badge
            key={filter}
            variant="outline"
            className={`cursor-pointer text-[10px] ${
              filters.priority.includes(filter) || filters.sources.includes(filter)
                ? 'bg-teal-600 text-white border-teal-500'
                : 'text-slate-400 border-slate-600 hover:bg-slate-700'
            }`}
            onClick={() => {
              const isPriority = ['critical', 'urgent', 'routine'].includes(filter);
              const key = isPriority ? 'priority' : 'sources';
              setFilters(prev => ({
                ...prev,
                [key]: prev[key].includes(filter)
                  ? prev[key].filter(f => f !== filter)
                  : [...prev[key], filter]
              }));
            }}
          >
            {filter}
          </Badge>
        ))}
      </div>
    </div>
  );
};

// Sheet wrapper for mobile/sidebar use
export const IncidentTimelineSheet = ({ trigger, onIncidentSelect }) => {
  return (
    <Sheet>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Timeline
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-[400px] p-0 glass-card border-slate-800">
        <IncidentTimelinePanel onIncidentSelect={onIncidentSelect} />
      </SheetContent>
    </Sheet>
  );
};

export default IncidentTimelinePanel;
